import { type ReactNode, useContext, useId } from 'react';
import {
    type Control,
    Controller,
    type FieldPath,
    type FieldValues,
    get,
    useFormState,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import ChipListInput from '@/components/atoms/ChipListInput';
import FormField from '@/components/atoms/FormField';
import {
    INTEGER_ITEM_PATTERN,
    isListKind,
    LIST_MAX_ITEMS,
    type ProviderFieldSchema,
} from '@/domain/providerConfig';

import { shownValue } from './enumChoices';
import { fieldLabelSuffix } from './FieldLabel';
import {
    FieldWarnings,
    fieldHint,
    type UnreadableStored,
    unreadableIds,
    viewerNotes,
} from './FieldWarnings';
import { ClientAdminViewContext } from './fieldContexts';
import { errorKeyOf, judgeWritten, listOf, type ProviderFieldConflict, textOf } from './fieldText';
import { fieldTexts } from './fieldTexts';
import { HeldBackFieldsContext } from './heldBackFields';
import { type ItemIdBinding, ScadaItemsEditor } from './ScadaItemsEditor';
import { SchemaValueInput } from './SchemaValueInput';
import { valueClassOf } from './valueClass';

import styles from './ProviderSchemaField.module.scss';

export type FormValue = string | string[];

/** A text field's value and setter, for something under the input such as a picker. */
export interface FieldBinding {
    value: string;
    onChange: (value: string) => void;
}

export interface ProviderSchemaFieldProps<T extends FieldValues> {
    field: ProviderFieldSchema;
    control: Control<T>;
    name: FieldPath<T>;
    disabled?: boolean;
    placeholder?: string;
    conflict?: ProviderFieldConflict | null;
    /** A function gets the binding to edit, so read-only leaves it out; a node always shows. */
    trailing?: ReactNode | ((binding: FieldBinding) => ReactNode);
    /** Replaces the schema's label, e.g. with a pen's name for its own value. */
    label?: string;
    /** After each edit, with the value before it. */
    onValueChange?: (next: FormValue, previous: FormValue) => void;
    /** For a SCADA list: offered under each entry's item id. */
    itemPicker?: (binding: ItemIdBinding) => ReactNode;
    /** The stored key, shown next to the label for OptoScale admins. */
    dbKey?: string;
    /** A stored value this field cannot read; shown so it is not mistaken for empty. */
    unreadable?: readonly UnreadableStored[];
}

/** One provider setting as its schema kind says; secrets have their own input. */
function ProviderSchemaField<T extends FieldValues>({
    field: given,
    control,
    name,
    disabled = false,
    placeholder,
    conflict = null,
    trailing,
    label,
    onValueChange,
    itemPicker,
    dbKey,
    unreadable = [],
}: ProviderSchemaFieldProps<T>) {
    // Read-only (still focusable, so its note is read) and asking for nothing: a box under a
    // stored value that is not an object, and an advanced setting, which only OptoScale changes.
    const clientAdmin = useContext(ClientAdminViewContext);
    const notes = viewerNotes(unreadable, clientAdmin);
    const readOnly = notes.some((entry) => entry.blocked) || (given.advanced && clientAdmin);
    const field = readOnly ? { ...given, required: false } : given;
    const { t } = useTranslation();
    // React's ids hold colons, which break the `#id` selectors jsdom and some libraries build.
    const id = useId().replaceAll(':', '');
    const heldBack = useContext(HeldBackFieldsContext);
    const { defaultValues } = useFormState({ control, name });
    if (field.kind === 'secret') return null;

    const texts = fieldTexts(field, t, label, placeholder);
    const { label: text, defaultText, placeholder: shownPlaceholder } = texts;
    // A read-only box asks for nothing, so it is neither required nor optional.
    const labelSuffix = fieldLabelSuffix({ optional: !field.required && !readOnly, dbKey }, t);
    const hint = fieldHint(field, defaultText, t);
    const describedBy = unreadableIds(id, notes).join(' ') || undefined;

    return (
        <Controller
            control={control}
            name={name}
            rules={{
                validate: (value: unknown) => judgeWritten(field, value, get(defaultValues, name)),
            }}
            render={({ field: input, fieldState }) => {
                const error = fieldState.error ? t(errorKeyOf(fieldState.error.message)) : null;
                function change(next: FormValue): void {
                    if (readOnly) return;
                    const previous: FormValue = isListKind(field.kind)
                        ? listOf(input.value)
                        : textOf(input.value);
                    input.onChange(next);
                    onValueChange?.(next, previous);
                }
                const binding: FieldBinding = { value: textOf(input.value), onChange: change };

                function renderField(): ReactNode {
                    // Read-only, any kind is one box: the field reads nothing it can show.
                    if (field.kind === 'scadaItems' && !readOnly)
                        return (
                            <ScadaItemsEditor
                                id={id}
                                label={text}
                                labelSuffix={labelSuffix}
                                value={textOf(input.value)}
                                onChange={change}
                                signals={field.enumValues}
                                disabled={disabled}
                                hint={hint}
                                describedBy={describedBy}
                                error={error}
                                itemPicker={itemPicker}
                                onHeldBackChange={(held) => heldBack?.setHeld(name, held)}
                            />
                        );
                    return (
                        <FormField
                            label={text}
                            htmlFor={id}
                            labelSuffix={labelSuffix}
                            hint={hint}
                            error={error}>
                            {isListKind(field.kind) && !readOnly ? (
                                <ChipListInput
                                    valueClassName={valueClassOf(field)}
                                    required={field.required}
                                    values={listOf(input.value)}
                                    onChange={change}
                                    maxItems={LIST_MAX_ITEMS}
                                    itemPattern={
                                        field.kind === 'integerList'
                                            ? INTEGER_ITEM_PATTERN
                                            : undefined
                                    }
                                    disabled={disabled}
                                    placeholder={shownPlaceholder}
                                />
                            ) : (
                                <SchemaValueInput
                                    valueClassName={valueClassOf(field)}
                                    field={readOnly ? { ...field, kind: 'text' } : field}
                                    readOnly={readOnly}
                                    name={input.name}
                                    value={shownValue(field, textOf(input.value), t, readOnly)}
                                    disabled={disabled}
                                    describedBy={describedBy}
                                    placeholder={shownPlaceholder}
                                    onBlur={input.onBlur}
                                    onChange={change}
                                />
                            )}
                        </FormField>
                    );
                }

                return (
                    <div className={styles.field}>
                        {renderField()}
                        <FieldWarnings
                            id={id}
                            field={field}
                            unreadable={notes}
                            conflict={conflict}
                        />
                        {typeof trailing === 'function' ? !readOnly && trailing(binding) : trailing}
                    </div>
                );
            }}
        />
    );
}

export default ProviderSchemaField;
export { ProviderSchemaField };
